import { Navbar, FeatureGrid, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, ShoppingCart, Package, FileText, BarChart3, Zap } from 'lucide-react';

const detailedFeatures = [
  {
    icon: Bot,
    title: 'AI-Powered WhatsApp Assistant',
    description: 'Our intelligent AI assistant handles customer conversations 24/7, answering product queries, processing orders, and providing support automatically.',
    benefits: [
      'Natural language understanding',
      'Multi-language support',
      'Custom training on your catalog',
      'Seamless handoff to human agents',
    ],
  },
  {
    icon: ShoppingCart,
    title: 'Smart Point of Sale',
    description: 'A fast, intuitive POS system designed for modern retail. Process sales quickly on tablets, desktops, or even phones.',
    benefits: [
      'Touch-optimized interface',
      'Barcode scanner support',
      'Multiple payment methods',
      'Offline mode capability',
    ],
  },
  {
    icon: Package,
    title: 'Product Catalog Management',
    description: 'Organize your entire product catalog with categories, variants, images, and detailed pricing all in one place.',
    benefits: [
      'Unlimited products',
      'Product variants',
      'Bulk import/export',
      'Image management',
    ],
  },
  {
    icon: FileText,
    title: 'GST-Compliant Invoicing',
    description: 'Generate professional, GST-compliant invoices automatically and send them directly via WhatsApp.',
    benefits: [
      'Automatic tax calculation',
      'Multiple invoice formats',
      'Digital signatures',
      'PDF generation',
    ],
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reporting',
    description: 'Get deep insights into your business with comprehensive reports on sales, customers, inventory, and AI performance.',
    benefits: [
      'Real-time dashboards',
      'Custom report builder',
      'Export capabilities',
      'Scheduled reports',
    ],
  },
  {
    icon: Zap,
    title: 'Workflow Automations',
    description: 'Set up powerful automations for order confirmations, follow-ups, review requests, and abandoned cart recovery.',
    benefits: [
      'Visual workflow builder',
      'Trigger-based actions',
      'WhatsApp templates',
      'Analytics tracking',
    ],
  },
];

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Powerful Features for{' '}
              <span className="gradient-text">Modern Commerce</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Everything you need to run a successful WhatsApp-first business, 
              from AI conversations to inventory management.
            </p>
            <Link to="/signup">
              <Button size="lg" className="rounded-full">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Detailed Features */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="space-y-24">
            {detailedFeatures.map((feature, index) => {
              const Icon = feature.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={feature.title}
                  className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}
                >
                  {/* Image/Illustration */}
                  <div className="flex-1">
                    <div className="aspect-video rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                      <Icon className="w-24 h-24 text-primary/50" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold mb-4">{feature.title}</h2>
                    <p className="text-muted-foreground mb-6">{feature.description}</p>
                    <ul className="space-y-3">
                      {feature.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-center gap-3">
                          <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                            <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join thousands of businesses already using SparkCommerce to grow their WhatsApp sales.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup">
                <Button size="lg" className="rounded-full">
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" size="lg" className="rounded-full">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
